/**
 * Theme state — light / dark / system, persisted in localStorage.
 *
 * The browser-side inline pre-paint script in each app's index.html is the
 * source of truth on FIRST PAINT (it reads the same localStorage key and
 * applies `data-theme` before React mounts). This hook then takes over,
 * mirrors that state into React, and reacts to user toggles + OS preference
 * changes when in `system` mode.
 *
 * Singleton store: all `useTheme()` calls share the same state via
 * `useSyncExternalStore`, so any component (toggle, language switch,
 * status indicator, …) sees the same truth.
 *
 * Emits a `clari:theme-changed` CustomEvent on every change so non-React
 * listeners (e.g. analytics) can react.
 */
import { useCallback, useSyncExternalStore } from 'react';
export const THEME_STORAGE_KEY = 'clari-theme';
export const THEME_EVENT_NAME = 'clari:theme-changed';
const DARK_QUERY = '(prefers-color-scheme: dark)';
let currentTheme = 'light';
let currentResolved = 'light';
let initialized = false;
let mediaQuery = null;
const subscribers = new Set();
function isTheme(value) {
    return value === 'light' || value === 'dark' || value === 'system';
}
function getSystemTheme() {
    if (typeof window === 'undefined')
        return 'light';
    return window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light';
}
function resolveTheme(theme) {
    return theme === 'system' ? getSystemTheme() : theme;
}
function applyToDom(resolved) {
    if (typeof document === 'undefined')
        return;
    document.documentElement.setAttribute('data-theme', resolved);
}
function readStoredTheme() {
    if (typeof window === 'undefined')
        return null;
    try {
        const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
        return isTheme(raw) ? raw : null;
    }
    catch {
        return null;
    }
}
function writeStoredTheme(theme) {
    if (typeof window === 'undefined')
        return;
    try {
        window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    }
    catch {
        // private mode / quota exceeded — non-fatal, theme still applies in-memory
    }
}
function notify() {
    for (const cb of subscribers)
        cb();
    if (typeof window === 'undefined')
        return;
    const detail = {
        theme: currentTheme,
        resolvedTheme: currentResolved,
    };
    window.dispatchEvent(new CustomEvent(THEME_EVENT_NAME, { detail }));
}
function handleSystemChange() {
    if (currentTheme !== 'system')
        return;
    const nextResolved = getSystemTheme();
    if (nextResolved === currentResolved)
        return;
    currentResolved = nextResolved;
    applyToDom(currentResolved);
    notify();
}
function ensureInitialized(defaultMode) {
    if (initialized)
        return;
    if (typeof window === 'undefined') {
        currentTheme = defaultMode;
        currentResolved = defaultMode;
        return;
    }
    const stored = readStoredTheme();
    currentTheme = stored ?? defaultMode;
    currentResolved = resolveTheme(currentTheme);
    applyToDom(currentResolved);
    mediaQuery = window.matchMedia(DARK_QUERY);
    mediaQuery.addEventListener('change', handleSystemChange);
    initialized = true;
}
function subscribe(cb) {
    subscribers.add(cb);
    return () => {
        subscribers.delete(cb);
    };
}
function setThemeInternal(next) {
    currentTheme = next;
    currentResolved = resolveTheme(next);
    writeStoredTheme(next);
    applyToDom(currentResolved);
    notify();
}
export function useTheme(options) {
    const defaultMode = options?.defaultMode ?? 'light';
    ensureInitialized(defaultMode);
    const theme = useSyncExternalStore(subscribe, () => currentTheme, () => defaultMode);
    const resolvedTheme = useSyncExternalStore(subscribe, () => currentResolved, () => defaultMode);
    const setTheme = useCallback((next) => {
        setThemeInternal(next);
    }, []);
    return { theme, resolvedTheme, setTheme };
}
//# sourceMappingURL=useTheme.js.map